import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { toPersianDigits } from '@saay/formatting';
import { useCombobox, useMultipleSelection, useSelect } from 'downshift';
import {
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type InputHTMLAttributes,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  chipCounterLabel,
  filterCityList,
  filterLabeledOptions,
  formatCityLabel,
  type CityRow,
  type LabeledValue,
  splitChipPaste,
} from './headless/selection-search.js';
import { FieldSupportSlot, revealTruncatedLabel } from './field-support.js';
import { Icon } from './icons.js';
import { PickerSheet, PickerTrigger, useCompactPicker } from './picker-sheet.js';

/**
 * The open list of a desktop select or combobox. It renders in a portal over the page so a table,
 * card, or dialog with hidden overflow never clips it (owner), sized to the field and flipped above it
 * when there is no room below. Downshift keeps focus and the keyboard; this only places the list.
 */
function FloatingList({
  anchor,
  children,
  open,
}: {
  readonly anchor: ReactNode;
  readonly children: ReactNode;
  readonly open: boolean;
}) {
  return (
    <PopoverPrimitive.Root open={open}>
      <PopoverPrimitive.Anchor asChild>{anchor}</PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          className="saay-select-popover"
          collisionPadding={8}
          dir="rtl"
          onCloseAutoFocus={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
          sideOffset={4}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function persianA11yStatus(state: {
  readonly highlightedIndex: number;
  readonly isOpen: boolean;
  readonly items?: readonly string[];
  readonly selectedItem: string | null | undefined;
}): string {
  if (!state.isOpen) {
    return '';
  }
  const resultCount = state.items?.length ?? 0;
  if (resultCount === 0) {
    return 'نتیجه‌ای نیست';
  }
  if (state.highlightedIndex >= 0) {
    return `${toPersianDigits(String(state.highlightedIndex + 1))} از ${toPersianDigits(String(resultCount))}`;
  }
  if (state.selectedItem !== null && state.selectedItem !== undefined) {
    return state.selectedItem;
  }
  return `${toPersianDigits(String(resultCount))} نتیجه`;
}

/* ——— S1 Checkbox ——— */

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly indeterminate?: boolean;
  readonly label: ReactNode;
}

/** MD3 checkbox with indeterminate and error states (S1). */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(props, ref) {
  const {
    checked,
    className,
    defaultChecked,
    disabled,
    error,
    hint,
    id,
    indeterminate,
    label,
    onChange,
    ...rest
  } = props;
  // A checkbox wired to validation (an `error` prop, even undefined) keeps its message slot.
  const reserveSupport = 'error' in props;
  const generated = useId();
  const fieldId = id ?? generated;
  const hintId = hint === undefined ? undefined : `${fieldId}-hint`;
  const errorId = error === undefined ? undefined : `${fieldId}-error`;
  // The error replaces the hint in the support slot, so it replaces it in the description too.
  const describedBy = errorId ?? hintId;
  const nativeRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (nativeRef.current) {
      nativeRef.current.indeterminate = indeterminate === true;
    }
  }, [indeterminate, checked]);

  const radixChecked =
    indeterminate === true
      ? 'indeterminate'
      : checked === true
        ? true
        : checked === false
          ? false
          : undefined;

  return (
    <div
      className={['saay-md-checkbox', className].filter(Boolean).join(' ')}
      data-disabled={disabled || undefined}
      data-error={error !== undefined || undefined}
    >
      <CheckboxPrimitive.Root
        aria-describedby={describedBy}
        aria-invalid={error === undefined ? undefined : true}
        className="saay-md-checkbox__control"
        disabled={disabled}
        id={fieldId}
        onCheckedChange={(next) => {
          const value = next === 'indeterminate' ? false : next === true;
          const native = nativeRef.current;
          if (native) {
            native.checked = value;
            const changeEvent = {
              currentTarget: native,
              target: native,
            } as ChangeEvent<HTMLInputElement>;
            onChange?.(changeEvent);
            native.dispatchEvent(new Event('input', { bubbles: true }));
            native.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            onChange?.({
              target: { checked: value, value: value ? 'on' : 'off' },
              currentTarget: { checked: value, value: value ? 'on' : 'off' },
            } as ChangeEvent<HTMLInputElement>);
          }
        }}
        {...(checked !== undefined || indeterminate === true
          ? { checked: radixChecked as boolean | 'indeterminate' }
          : defaultChecked !== undefined
            ? { defaultChecked }
            : {})}
      >
        {/* The mark is drawn on the 18 px box itself (MD3), so it stays centered in every engine. */}
        <CheckboxPrimitive.Indicator className="saay-md-checkbox__indicator">
          <svg aria-hidden="true" focusable="false" viewBox="0 0 18 18">
            {indeterminate === true ? (
              <path d="M4.5 9h9" />
            ) : (
              <path d="M3.75 9.125 7.25 12.625 14.25 5.375" />
            )}
          </svg>
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      <input
        {...rest}
        aria-hidden="true"
        className="saay-md-checkbox__native"
        {...(checked !== undefined ? { checked: checked === true } : {})}
        {...(checked === undefined && defaultChecked !== undefined ? { defaultChecked } : {})}
        disabled={disabled}
        onChange={onChange}
        ref={(node) => {
          nativeRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        tabIndex={-1}
        type="checkbox"
      />
      <label className="saay-md-checkbox__label" htmlFor={fieldId}>
        {label}
      </label>
      <FieldSupportSlot
        error={error}
        errorId={errorId}
        hint={hint}
        hintId={hintId}
        reserve={reserveSupport}
      />
    </div>
  );
});

/* ——— S2 Checkbox group ——— */

export interface CheckboxGroupOption {
  readonly disabled?: boolean;
  readonly label: ReactNode;
  readonly value: string;
}

export function CheckboxGroup(props: {
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  readonly legend: ReactNode;
  readonly onChange: (next: readonly string[]) => void;
  readonly options: readonly CheckboxGroupOption[];
  readonly searchPlaceholder?: string;
  readonly showSearchAbove?: number;
  readonly summary?: ReactNode;
  readonly value: readonly string[];
}) {
  const {
    disabled = false,
    error,
    legend,
    onChange,
    options,
    searchPlaceholder = 'جست‌وجو',
    showSearchAbove = 10,
    summary,
    value,
  } = props;
  const [query, setQuery] = useState('');
  const selected = new Set(value);
  const visible =
    options.length >= showSearchAbove && query.trim().length > 0
      ? options.filter(
          (option) => String(option.label).includes(query) || option.value.includes(query),
        )
      : options;

  function toggleOne(optionValue: string, nextChecked: boolean) {
    const next = new Set(selected);
    if (nextChecked) {
      next.add(optionValue);
    } else {
      next.delete(optionValue);
    }
    onChange([...next]);
  }

  function toggleAll(nextChecked: boolean) {
    if (!nextChecked) {
      onChange([]);
      return;
    }
    onChange(options.filter((option) => !option.disabled).map((option) => option.value));
  }

  const allEnabled = options.filter((option) => !option.disabled);
  const allSelected =
    allEnabled.length > 0 && allEnabled.every((option) => selected.has(option.value));
  const someSelected = allEnabled.some((option) => selected.has(option.value));

  return (
    <fieldset className="saay-checkbox-group" disabled={disabled}>
      <legend>{legend}</legend>
      {summary === undefined ? null : <p className="saay-checkbox-group__summary">{summary}</p>}
      {options.length >= showSearchAbove ? (
        <input
          aria-label={searchPlaceholder}
          className="saay-checkbox-group__search saay-textfield__control"
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          type="search"
          value={query}
        />
      ) : null}
      <Checkbox
        checked={allSelected}
        indeterminate={!allSelected && someSelected}
        label="همه"
        onChange={(event) => toggleAll(event.target.checked)}
      />
      {visible.map((option) => (
        <Checkbox
          checked={selected.has(option.value)}
          disabled={option.disabled}
          key={option.value}
          label={option.label}
          onChange={(event) => toggleOne(option.value, event.target.checked)}
        />
      ))}
      <FieldSupportSlot error={error} reserve={'error' in props} />
    </fieldset>
  );
}

/* ——— S3 Radio group ——— */

export interface RadioGroupOption {
  readonly description?: ReactNode;
  readonly disabled?: boolean;
  readonly label: ReactNode;
  readonly value: string;
}

export function RadioGroup(props: {
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly legend: ReactNode;
  readonly name?: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly RadioGroupOption[];
  readonly value: string | undefined;
}) {
  const { disabled = false, error, hint, legend, name, onChange, options, value } = props;
  // A group wired to validation (an `error` prop, even undefined) keeps its message slot.
  const reserveSupport = 'error' in props;
  const id = useId();
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  const errorId = error === undefined ? undefined : `${id}-error`;
  // The error replaces the hint in the support slot, so it replaces it in the description too.
  const describedBy = errorId ?? hintId;

  return (
    <fieldset className="saay-radio-group" disabled={disabled}>
      <legend>{legend}</legend>
      <RadioGroupPrimitive.Root
        aria-describedby={describedBy}
        aria-invalid={error === undefined ? undefined : true}
        name={name}
        onValueChange={onChange}
        value={value ?? ''}
      >
        {options.map((option) => (
          <label
            className="saay-radio-item"
            data-disabled={option.disabled || disabled || undefined}
            key={option.value}
          >
            <RadioGroupPrimitive.Item
              className="saay-radio-item__control"
              disabled={option.disabled || disabled}
              value={option.value}
            />
            <span className="saay-radio-item__label">
              {option.label}
              {option.description === undefined ? null : (
                <span className="saay-choice__description">{option.description}</span>
              )}
            </span>
          </label>
        ))}
      </RadioGroupPrimitive.Root>
      <FieldSupportSlot
        error={error}
        errorId={errorId}
        hint={hint}
        hintId={hintId}
        reserve={reserveSupport}
      />
    </fieldset>
  );
}

/* ——— S4 Switch ——— */

export interface SwitchProps {
  readonly checked: boolean;
  readonly disabled?: boolean;
  readonly id?: string;
  readonly label: ReactNode;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly pending?: boolean;
}

export function Switch({
  checked,
  disabled = false,
  id,
  label,
  onCheckedChange,
  pending = false,
}: SwitchProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <label
      className="saay-switch"
      data-disabled={disabled || undefined}
      data-pending={pending || undefined}
    >
      <SwitchPrimitive.Root
        checked={checked}
        className="saay-switch__track"
        disabled={disabled || pending}
        id={fieldId}
        onCheckedChange={onCheckedChange}
      >
        <SwitchPrimitive.Thumb className="saay-switch__thumb">
          {pending ? (
            <Icon name="refresh" />
          ) : checked ? (
            <Icon name="check" />
          ) : (
            <Icon name="close" />
          )}
        </SwitchPrimitive.Thumb>
      </SwitchPrimitive.Root>
      <span className="saay-switch__label">{label}</span>
    </label>
  );
}

/* ——— S5 Chips ——— */

export function FilterChip({
  children,
  onRemove,
  onToggle,
  selected = false,
  ...rest
}: {
  readonly children: ReactNode;
  readonly onRemove?: () => void;
  readonly onToggle?: () => void;
  readonly selected?: boolean;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) {
  const content = (
    <>
      {selected ? <Icon name="check" /> : null}
      {children}
    </>
  );
  if (onRemove === undefined) {
    return (
      <button
        {...rest}
        className="saay-chip"
        data-selected={selected || undefined}
        data-variant="filter"
        onClick={onToggle}
        type="button"
      >
        {content}
      </button>
    );
  }
  // Two sibling buttons: a button inside a button is not valid interactive content.
  const { className: _className, ...buttonProps } = rest;
  return (
    <span className="saay-chip" data-selected={selected || undefined} data-variant="filter">
      <button {...buttonProps} className="saay-chip__main" onClick={onToggle} type="button">
        {content}
      </button>
      <button aria-label="حذف فیلتر" className="saay-chip__remove" onClick={onRemove} type="button">
        <Icon name="close" />
      </button>
    </span>
  );
}

export function AssistChip({
  children,
  ...rest
}: { readonly children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...rest} className="saay-chip" data-variant="assist" type="button">
      {children}
    </button>
  );
}

export function SuggestionChip({
  children,
  ...rest
}: { readonly children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...rest} className="saay-chip" data-variant="suggestion" type="button">
      {children}
    </button>
  );
}

export function ChipRow({ children }: { readonly children: ReactNode }) {
  return <div className="saay-chip-row">{children}</div>;
}

/* ——— S6 Select menu ——— */

export interface SelectMenuProps {
  readonly disabled?: boolean;
  readonly disabledReason?: string;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly id?: string;
  readonly label: ReactNode;
  readonly onChange: (value: string) => void;
  readonly options: readonly LabeledValue[];
  readonly placeholder?: string;
  readonly value: string | undefined;
}

/** A single-choice menu field; phones open the option picker (PickerSheet) instead. */
export function SelectMenu(props: SelectMenuProps) {
  return useCompactPicker() ? <CompactSelectMenu {...props} /> : <DesktopSelectMenu {...props} />;
}

function CompactSelectMenu({
  disabled = false,
  disabledReason,
  error,
  hint,
  id,
  label,
  onChange,
  options,
  placeholder = 'انتخاب کنید',
  value,
}: SelectMenuProps) {
  const fieldId = useId();
  const controlId = id ?? fieldId;
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const readOnlySingle = options.length === 1 && !options[0]?.disabled;
  const effectiveValue = readOnlySingle ? options[0]?.value : value;
  const effectiveDisabled = disabled || options.length === 0 || readOnlySingle;

  useEffect(() => {
    if (readOnlySingle && options[0] !== undefined && value !== options[0].value) {
      onChange(options[0].value);
    }
  }, [onChange, options, readOnlySingle, value]);

  const hintId = hint === undefined ? undefined : `${controlId}-hint`;
  const errorId = error === undefined ? undefined : `${controlId}-error`;
  // The error replaces the hint in the support slot, so it replaces it in the description too.
  const describedBy = errorId ?? hintId;
  const selectedLabel = options.find((option) => option.value === effectiveValue)?.label;

  return (
    <div className="saay-select-field">
      <span
        className="saay-select-field__label"
        onPointerEnter={revealTruncatedLabel}
        id={`${controlId}-label`}
      >
        {label}
      </span>
      <PickerTrigger
        ref={triggerRef}
        aria-describedby={describedBy}
        aria-invalid={error === undefined ? undefined : true}
        aria-labelledby={`${controlId}-label ${controlId}`}
        className="saay-select-field__trigger"
        disabled={effectiveDisabled}
        id={controlId}
        invalid={error !== undefined}
        onClick={() => setOpen(true)}
        title={effectiveDisabled && !readOnlySingle ? (disabledReason ?? 'غیرفعال') : undefined}
      >
        {selectedLabel ?? placeholder}
      </PickerTrigger>
      <PickerSheet
        returnFocusRef={triggerRef}
        onOpenChange={setOpen}
        onPick={onChange}
        open={open}
        options={options}
        selected={effectiveValue === undefined ? [] : [effectiveValue]}
        title={label}
      />
      <FieldSupportSlot error={error} errorId={errorId} hint={hint} hintId={hintId} />
    </div>
  );
}

function DesktopSelectMenu({
  disabled = false,
  disabledReason,
  error,
  hint,
  id,
  label,
  onChange,
  options,
  placeholder = 'انتخاب کنید',
  value,
}: SelectMenuProps) {
  const fieldId = useId();
  const controlId = id ?? fieldId;
  const readOnlySingle = options.length === 1 && !options[0]?.disabled;
  const effectiveValue = readOnlySingle ? options[0]?.value : value;
  const effectiveDisabled = disabled || options.length === 0;

  useEffect(() => {
    if (readOnlySingle && options[0] !== undefined && value !== options[0].value) {
      onChange(options[0].value);
    }
  }, [onChange, options, readOnlySingle, value]);

  const items = useMemo(() => options.map((option) => option.label), [options]);
  const selectedIndex = options.findIndex((option) => option.value === effectiveValue);

  const { getItemProps, getMenuProps, getToggleButtonProps, highlightedIndex, isOpen } = useSelect({
    items,
    isItemDisabled: (_item, index) => options[index]?.disabled === true,
    selectedItem: selectedIndex >= 0 ? (items[selectedIndex] ?? null) : null,
    onSelectedItemChange: ({ selectedItem: picked }) => {
      if (picked === null || picked === undefined) {
        return;
      }
      const index = items.indexOf(picked);
      const option = options[index];
      if (option !== undefined) {
        onChange(option.value);
      }
    },
    getA11yStatusMessage: (state) => persianA11yStatus({ ...state, items }),
    ...(effectiveDisabled ? { isOpen: false } : {}),
  });

  const hintId = hint === undefined ? undefined : `${controlId}-hint`;
  const errorId = error === undefined ? undefined : `${controlId}-error`;
  // The error replaces the hint in the support slot, so it replaces it in the description too.
  const describedBy = errorId ?? hintId;
  const selectedLabel = options.find((option) => option.value === effectiveValue)?.label;

  return (
    <div className="saay-select-field">
      <span
        className="saay-select-field__label"
        onPointerEnter={revealTruncatedLabel}
        id={`${controlId}-label`}
      >
        {label}
      </span>
      <FloatingList
        anchor={
          <div style={{ position: 'relative' }}>
            <button
              {...getToggleButtonProps({
                'aria-describedby': describedBy,
                'aria-invalid': error === undefined ? undefined : true,
                'aria-labelledby': `${controlId}-label`,
                disabled: effectiveDisabled,
                id: controlId,
                title: effectiveDisabled ? (disabledReason ?? 'غیرفعال') : undefined,
                type: 'button',
              })}
              className="saay-select-field__trigger"
              data-invalid={error !== undefined || undefined}
            >
              <span>{selectedLabel ?? placeholder}</span>
              {readOnlySingle ? null : <Icon name="chevron-down" />}
            </button>
          </div>
        }
        open={isOpen && !readOnlySingle}
      >
        <ul {...getMenuProps({}, { suppressRefError: true })} className="saay-select-menu">
          {isOpen
            ? items.map((item, index) => {
                const option = options[index];
                return (
                  <li
                    {...getItemProps({ item, index })}
                    className="saay-select-menu__item"
                    data-highlighted={highlightedIndex === index || undefined}
                    data-selected={option?.value === effectiveValue || undefined}
                    key={option?.value ?? item}
                  >
                    {option?.value === effectiveValue ? <Icon name="check" /> : null}
                    {item}
                  </li>
                );
              })
            : null}
        </ul>
      </FloatingList>
      <FieldSupportSlot error={error} errorId={errorId} hint={hint} hintId={hintId} />
    </div>
  );
}

/* ——— S7 Combobox ——— */

export interface ComboboxProps {
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly id?: string;
  readonly inputValue?: string;
  readonly label: ReactNode;
  readonly loading?: boolean;
  readonly onChange: (value: string) => void;
  readonly onInputValueChange?: (value: string) => void;
  readonly options: readonly LabeledValue[];
  /**
   * `false` when the caller already searched (a server lookup by mobile or code): the typed text
   * need not appear in an option's label, so the options are offered as they are.
   */
  readonly filterOptions?: boolean;
  readonly placeholder?: string;
  /**
   * The label of the selected value when `options` is a filtered view of a longer list (the typed
   * text hides the selected row). Without it the selection follows `options`, which would flip
   * between selected and not selected while typing.
   */
  readonly selectedLabel?: string | null;
  readonly value: string | undefined;
}

/**
 * Focus already opens the menu; the default input click would toggle it shut again right after,
 * so a click only (re)opens it.
 */
function keepMenuOpenOnClick(openMenu: () => void) {
  return (event: ReactMouseEvent<HTMLInputElement>) => {
    (event.nativeEvent as { preventDownshiftDefault?: boolean }).preventDownshiftDefault = true;
    openMenu();
  };
}

/** A searchable single-choice field; phones open the full-screen picker (PickerSheet) instead. */
export function Combobox(props: ComboboxProps) {
  return useCompactPicker() ? <CompactCombobox {...props} /> : <DesktopCombobox {...props} />;
}

function CompactCombobox({
  disabled = false,
  error,
  hint,
  id,
  inputValue: controlledInput,
  label,
  loading = false,
  onChange,
  onInputValueChange,
  options,
  filterOptions = true,
  placeholder = 'جست‌وجو یا انتخاب',
  selectedLabel,
  value,
}: ComboboxProps) {
  const fieldId = useId();
  const controlId = id ?? fieldId;
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [uncontrolledInput, setUncontrolledInput] = useState('');
  const inputValue = controlledInput ?? uncontrolledInput;
  const setInput = (next: string) => {
    if (onInputValueChange) {
      onInputValueChange(next);
    } else {
      setUncontrolledInput(next);
    }
  };
  const selectedOption = options.find((option) => option.value === value);
  const selectedText =
    selectedLabel === undefined ? (selectedOption?.label ?? null) : selectedLabel;
  // While the picker is open the field text is the search; the selected label means "all".
  const query = open && inputValue === selectedText ? '' : inputValue;
  const filtered = useMemo(
    () => (!filterOptions ? options : filterLabeledOptions(options, query)),
    [filterOptions, options, query],
  );

  function openPicker(): void {
    // Start with an empty search (the whole list), not the selected label.
    if (selectedText !== null && inputValue === selectedText) setInput('');
    setOpen(true);
  }

  function closePicker(): void {
    setOpen(false);
    setInput(selectedText ?? '');
  }

  const hintId = hint === undefined ? undefined : `${controlId}-hint`;
  const errorId = error === undefined ? undefined : `${controlId}-error`;
  // The error replaces the hint in the support slot, so it replaces it in the description too.
  const describedBy = errorId ?? hintId;

  return (
    <div className="saay-combobox-field">
      <label
        className="saay-combobox-field__label"
        onPointerEnter={revealTruncatedLabel}
        htmlFor={controlId}
      >
        {label}
      </label>
      <PickerTrigger
        ref={triggerRef}
        aria-describedby={describedBy}
        aria-invalid={error === undefined ? undefined : true}
        className="saay-combobox-field__input-wrap saay-combobox-field__trigger"
        data-empty={selectedText === null || undefined}
        disabled={disabled}
        id={controlId}
        invalid={error !== undefined}
        onClick={openPicker}
      >
        {selectedText ?? placeholder}
      </PickerTrigger>
      <PickerSheet
        returnFocusRef={triggerRef}
        onOpenChange={(next) => (next ? openPicker() : closePicker())}
        onPick={(picked) => {
          const option = filtered.find((candidate) => candidate.value === picked);
          onChange(picked);
          setOpen(false);
          setInput(option?.label ?? '');
        }}
        open={open}
        options={filtered}
        search={{
          autoFocus: true,
          loading,
          onChange: setInput,
          placeholder,
          value: query,
        }}
        selected={value === undefined ? [] : [value]}
        title={label}
      />
      <FieldSupportSlot error={error} errorId={errorId} hint={hint} hintId={hintId} />
    </div>
  );
}

function DesktopCombobox({
  disabled = false,
  error,
  hint,
  id,
  inputValue: controlledInput,
  label,
  loading = false,
  onChange,
  onInputValueChange,
  options,
  filterOptions = true,
  placeholder = 'جست‌وجو یا انتخاب',
  selectedLabel,
  value,
}: ComboboxProps) {
  const fieldId = useId();
  const controlId = id ?? fieldId;
  const [uncontrolledInput, setUncontrolledInput] = useState('');
  const inputValue = controlledInput ?? uncontrolledInput;
  // The field starts with the selected label; until it is edited, the whole list stays offered.
  const showAll =
    selectedLabel !== undefined && selectedLabel !== null && inputValue === selectedLabel;
  const filtered = useMemo(
    () => (showAll || !filterOptions ? options : filterLabeledOptions(options, inputValue)),
    [filterOptions, inputValue, options, showAll],
  );
  const items = useMemo(() => filtered.map((option) => option.label), [filtered]);
  const selectedOption = options.find((option) => option.value === value);
  const selectedItem =
    selectedLabel === undefined ? (selectedOption?.label ?? null) : selectedLabel;

  const {
    getInputProps,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    isOpen,
    openMenu,
    selectItem,
  } = useCombobox({
    items,
    inputValue,
    isItemDisabled: (_item, index) => filtered[index]?.disabled === true,
    selectedItem,
    onInputValueChange: ({ inputValue: next }) => {
      if (onInputValueChange) {
        onInputValueChange(next);
      } else {
        setUncontrolledInput(next);
      }
    },
    onSelectedItemChange: ({ selectedItem: picked }) => {
      if (picked === null || picked === undefined) {
        return;
      }
      const index = items.indexOf(picked);
      const option = filtered[index];
      if (option !== undefined) {
        onChange(option.value);
        if (onInputValueChange) {
          onInputValueChange(option.label);
        } else {
          setUncontrolledInput(option.label);
        }
      }
    },
    onIsOpenChange: ({ isOpen: nextOpen }) => {
      const only = items[0];
      if (nextOpen && only !== undefined && items.length === 1 && inputValue.trim().length > 0) {
        selectItem(only);
      }
    },
    getA11yStatusMessage: (state) => persianA11yStatus({ ...state, items }),
    itemToString: (item) => item ?? '',
  });

  const hintId = hint === undefined ? undefined : `${controlId}-hint`;
  const errorId = error === undefined ? undefined : `${controlId}-error`;
  // The error replaces the hint in the support slot, so it replaces it in the description too.
  const describedBy = errorId ?? hintId;

  return (
    <div className="saay-combobox-field">
      <label
        className="saay-combobox-field__label"
        onPointerEnter={revealTruncatedLabel}
        htmlFor={controlId}
      >
        {label}
      </label>
      <FloatingList
        anchor={
          <div
            className="saay-combobox-field__input-wrap"
            data-invalid={error !== undefined || undefined}
          >
            <input
              {...getInputProps({
                'aria-describedby': describedBy,
                'aria-invalid': error === undefined ? undefined : true,
                disabled,
                id: controlId,
                placeholder,
                onClick: keepMenuOpenOnClick(openMenu),
                onFocus: () => openMenu(),
              })}
              className="saay-combobox-field__input"
            />
            {loading ? <Icon name="refresh" /> : <Icon name="search" />}
          </div>
        }
        open={isOpen}
      >
        <ul {...getMenuProps({}, { suppressRefError: true })} className="saay-select-menu">
          {isOpen && items.length === 0 ? (
            <li className="saay-select-menu__empty">نتیجه‌ای نیست</li>
          ) : (
            items.map((item, index) => {
              const option = filtered[index];
              return (
                <li
                  {...getItemProps({ item, index })}
                  className="saay-select-menu__item"
                  data-highlighted={highlightedIndex === index || undefined}
                  data-selected={option?.value === value || undefined}
                  key={option?.value ?? item}
                >
                  {option?.value === value ? <Icon name="check" /> : null}
                  {item}
                </li>
              );
            })
          )}
        </ul>
      </FloatingList>
      <FieldSupportSlot error={error} errorId={errorId} hint={hint} hintId={hintId} />
    </div>
  );
}

export interface CityComboboxProps {
  readonly cities: readonly CityRow[];
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly id?: string;
  readonly label?: ReactNode;
  readonly onChange: (cityId: string) => void;
  readonly placeholder?: string;
  readonly value: string | undefined;
}

/** City picker with province-aware search (D-76, S7 / X8). */
export function CityCombobox({
  cities,
  disabled = false,
  error,
  hint,
  id,
  label = 'شهر',
  onChange,
  placeholder = 'شهر یا استان',
  value,
}: CityComboboxProps) {
  const [inputValue, setInputValue] = useState(() => {
    const row = cities.find((city) => city.cityId === value);
    return row === undefined ? '' : formatCityLabel(row);
  });

  const selectedRow = useMemo(() => cities.find((city) => city.cityId === value), [cities, value]);

  useEffect(() => {
    setInputValue(selectedRow === undefined ? '' : formatCityLabel(selectedRow));
  }, [selectedRow]);

  const options = useMemo(
    () =>
      filterCityList(
        cities,
        selectedRow !== undefined && inputValue === formatCityLabel(selectedRow) ? '' : inputValue,
      )
        .filter((entry): entry is { kind: 'city'; row: CityRow } => entry.kind === 'city')
        .map((entry) => ({
          label: formatCityLabel(entry.row),
          value: entry.row.cityId,
        })),
    [cities, inputValue, selectedRow],
  );

  return (
    <Combobox
      disabled={disabled}
      error={error}
      hint={hint}
      inputValue={inputValue}
      label={label}
      onChange={onChange}
      onInputValueChange={setInputValue}
      options={options}
      placeholder={placeholder}
      selectedLabel={selectedRow === undefined ? null : formatCityLabel(selectedRow)}
      value={value}
      {...(id === undefined ? {} : { id })}
    />
  );
}

/* ——— S8 Multi-select ——— */

export interface MultiSelectComboboxProps {
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly label: ReactNode;
  readonly onChange: (next: readonly string[]) => void;
  readonly options: readonly LabeledValue[];
  readonly placeholder?: string;
  readonly value: readonly string[];
}

/** A multi-choice field with chips; phones open the option picker (PickerSheet) instead. */
export function MultiSelectCombobox(props: MultiSelectComboboxProps) {
  return useCompactPicker() ? (
    <CompactMultiSelect {...props} />
  ) : (
    <DesktopMultiSelectCombobox {...props} />
  );
}

function CompactMultiSelect({
  disabled = false,
  error,
  hint,
  label,
  onChange,
  options,
  placeholder = 'انتخاب چندتایی',
  value,
}: MultiSelectComboboxProps) {
  const fieldId = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const selectedItems = options.filter((option) => value.includes(option.value));
  const hintId = hint === undefined ? undefined : `${fieldId}-hint`;
  const errorId = error === undefined ? undefined : `${fieldId}-error`;
  // The error replaces the hint in the support slot, so it replaces it in the description too.
  const describedBy = errorId ?? hintId;

  return (
    <div className="saay-chips-input">
      <label
        className="saay-combobox-field__label"
        onPointerEnter={revealTruncatedLabel}
        htmlFor={fieldId}
      >
        {label}
      </label>
      <div className="saay-chips-input__box" data-invalid={error !== undefined || undefined}>
        {selectedItems.map((option) => (
          <span
            className="saay-chip saay-chips-input__chip"
            data-variant="filter"
            key={option.value}
          >
            {option.label}
            <button
              aria-label={`حذف ${option.label}`}
              className="saay-chip__remove"
              disabled={disabled}
              onClick={() => onChange(value.filter((item) => item !== option.value))}
              type="button"
            >
              <Icon name="close" />
            </button>
          </span>
        ))}
        <PickerTrigger
          ref={triggerRef}
          aria-describedby={describedBy}
          aria-invalid={error === undefined ? undefined : true}
          className="saay-chips-input__trigger"
          disabled={disabled}
          id={fieldId}
          invalid={error !== undefined}
          onClick={() => setOpen(true)}
        >
          {placeholder}
        </PickerTrigger>
      </div>
      <PickerSheet
        returnFocusRef={triggerRef}
        multiple
        onOpenChange={setOpen}
        onPick={(picked) =>
          onChange(
            value.includes(picked) ? value.filter((item) => item !== picked) : [...value, picked],
          )
        }
        open={open}
        options={options}
        selected={value}
        title={label}
      />
      <FieldSupportSlot error={error} errorId={errorId} hint={hint} hintId={hintId} />
    </div>
  );
}

function DesktopMultiSelectCombobox({
  disabled = false,
  error,
  hint,
  label,
  onChange,
  options,
  placeholder = 'انتخاب چندتایی',
  value,
}: MultiSelectComboboxProps) {
  const selectedItems = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value],
  );
  const { addSelectedItem, getDropdownProps, getSelectedItemProps } = useMultipleSelection({
    selectedItems,
  });

  const [inputValue, setInputValue] = useState('');
  const filtered = useMemo(
    () =>
      filterLabeledOptions(options, inputValue).filter((option) => !value.includes(option.value)),
    [inputValue, options, value],
  );
  const items = useMemo(() => filtered.map((option) => option.label), [filtered]);
  const fieldId = useId();

  const {
    getInputProps,
    getItemProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    isOpen,
    openMenu,
  } = useCombobox({
    items,
    inputValue,
    labelId: fieldId,
    selectedItem: null,
    onInputValueChange: ({ inputValue: next }) => setInputValue(next),
    onSelectedItemChange: ({ selectedItem: picked }) => {
      if (picked === null || picked === undefined) {
        return;
      }
      const index = items.indexOf(picked);
      const option = filtered[index];
      if (option !== undefined && !value.includes(option.value)) {
        addSelectedItem(option);
        onChange([...value, option.value]);
        setInputValue('');
      }
    },
    getA11yStatusMessage: (state) => persianA11yStatus({ ...state, items }),
    stateReducer: (_state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return { ...changes, isOpen: true };
        default:
          return changes;
      }
    },
  });

  const hintId = hint === undefined ? undefined : `${fieldId}-hint`;
  const errorId = error === undefined ? undefined : `${fieldId}-error`;
  // The error replaces the hint in the support slot, so it replaces it in the description too.
  const describedBy = errorId ?? hintId;

  return (
    <div className="saay-chips-input">
      <span
        {...getLabelProps()}
        className="saay-combobox-field__label"
        onPointerEnter={revealTruncatedLabel}
      >
        {label}
      </span>
      <FloatingList
        anchor={
          <div className="saay-chips-input__box" data-invalid={error !== undefined || undefined}>
            {selectedItems.map((option, index) => (
              <span
                {...getSelectedItemProps({ selectedItem: option, index })}
                className="saay-chip saay-chips-input__chip"
                data-variant="filter"
                key={option.value}
              >
                {option.label}
                <button
                  aria-label={`حذف ${option.label}`}
                  className="saay-chip__remove"
                  onClick={(event) => {
                    event.stopPropagation();
                    onChange(value.filter((item) => item !== option.value));
                  }}
                  type="button"
                >
                  <Icon name="close" />
                </button>
              </span>
            ))}
            <input
              {...getInputProps(
                getDropdownProps({
                  'aria-describedby': describedBy,
                  'aria-invalid': error === undefined ? undefined : true,
                  disabled,
                  placeholder,
                  onClick: keepMenuOpenOnClick(openMenu),
                  onFocus: () => openMenu(),
                }),
              )}
              className="saay-chips-input__field"
            />
          </div>
        }
        open={isOpen}
      >
        <ul {...getMenuProps({}, { suppressRefError: true })} className="saay-select-menu">
          {isOpen && items.length === 0 ? (
            <li className="saay-select-menu__empty">نتیجه‌ای نیست</li>
          ) : (
            items.map((item, index) => (
              <li
                {...getItemProps({ item, index })}
                className="saay-select-menu__item"
                data-highlighted={highlightedIndex === index || undefined}
                key={item}
              >
                {item}
              </li>
            ))
          )}
        </ul>
      </FloatingList>
      <FieldSupportSlot error={error} errorId={errorId} hint={hint} hintId={hintId} />
    </div>
  );
}

/* ——— I10 Chips input ——— */

export function ChipsInput({
  disabled = false,
  error,
  hint,
  label,
  maxItems,
  onChange,
  suggestions = [],
  validateItem,
  value,
}: {
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly label: ReactNode;
  readonly maxItems: number;
  readonly onChange: (next: readonly string[]) => void;
  readonly suggestions?: readonly LabeledValue[];
  readonly validateItem?: (item: string) => string | undefined;
  readonly value: readonly string[];
}) {
  const items = value.map((token) => ({ label: token, value: token }));
  const { addSelectedItem, getDropdownProps, getSelectedItemProps, removeSelectedItem } =
    useMultipleSelection({
      selectedItems: items,
      onStateChange: ({ selectedItems: next, type }) => {
        if (type === useMultipleSelection.stateChangeTypes.FunctionSetSelectedItems && next) {
          onChange(next.map((item) => item.value));
        }
      },
    });

  const [inputValue, setInputValue] = useState('');
  const suggestionItems = useMemo(
    () =>
      suggestions
        .filter((option) => !value.includes(option.value))
        .filter((option) => filterLabeledOptions([option], inputValue).length > 0)
        .map((option) => option.label),
    [inputValue, suggestions, value],
  );

  const fieldId = useId();

  const {
    getInputProps,
    getItemProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    isOpen,
    openMenu,
  } = useCombobox({
    items: suggestionItems,
    inputValue,
    labelId: fieldId,
    selectedItem: null,
    onInputValueChange: ({ inputValue: next }) => setInputValue(next),
    onStateChange: ({ inputValue: nextInput, type }) => {
      const text = nextInput ?? '';
      if (type === useCombobox.stateChangeTypes.InputKeyDownEnter && text.trim().length > 0) {
        commitTokens(text);
      }
      if (type === useCombobox.stateChangeTypes.InputBlur && text.trim().length > 0) {
        commitTokens(text);
      }
    },
    onSelectedItemChange: ({ selectedItem: picked }) => {
      if (picked === null || picked === undefined) {
        return;
      }
      commitTokens(picked);
    },
    getA11yStatusMessage: (state) => persianA11yStatus({ ...state, items: suggestionItems }),
  });

  function commitTokens(raw: string) {
    const parts = splitChipPaste(raw);
    if (parts.length === 0) {
      return;
    }
    const next = [...value];
    for (const part of parts) {
      if (next.length >= maxItems) {
        break;
      }
      if (!next.includes(part)) {
        next.push(part);
      }
    }
    onChange(next);
    setInputValue('');
  }

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace' && inputValue.length === 0 && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  const hintId = hint === undefined ? undefined : `${fieldId}-hint`;
  const errorId = error === undefined ? undefined : `${fieldId}-error`;
  // The error replaces the hint in the support slot, so it replaces it in the description too.
  const describedBy = errorId ?? hintId;
  const counter = chipCounterLabel(value.length, maxItems);

  return (
    <div className="saay-chips-input">
      <span
        {...getLabelProps()}
        className="saay-combobox-field__label"
        onPointerEnter={revealTruncatedLabel}
      >
        {label}
      </span>
      <FloatingList
        anchor={
          <div className="saay-chips-input__box" data-invalid={error !== undefined || undefined}>
            {items.map((item, index) => {
              const invalidReason = validateItem?.(item.value);
              return (
                <span
                  {...getSelectedItemProps({ selectedItem: item, index })}
                  className="saay-chip saay-chips-input__chip"
                  data-invalid={invalidReason !== undefined || undefined}
                  data-variant="filter"
                  key={item.value}
                  title={invalidReason}
                >
                  {item.label}
                  <button
                    aria-label={`حذف ${item.label}`}
                    className="saay-chip__remove"
                    disabled={disabled}
                    onClick={() => {
                      removeSelectedItem(item);
                      onChange(value.filter((token) => token !== item.value));
                    }}
                    type="button"
                  >
                    <Icon name="close" />
                  </button>
                </span>
              );
            })}
            <input
              {...getInputProps(
                getDropdownProps({
                  'aria-describedby': describedBy,
                  'aria-invalid': error === undefined ? undefined : true,
                  disabled: disabled || value.length >= maxItems,
                  onClick: keepMenuOpenOnClick(openMenu),
                  onFocus: () => openMenu(),
                  onKeyDown: onInputKeyDown,
                  onPaste: (event) => {
                    const text = event.clipboardData.getData('text');
                    commitTokens(text);
                    event.preventDefault();
                  },
                }),
              )}
              className="saay-chips-input__field"
            />
          </div>
        }
        open={isOpen}
      >
        <ul {...getMenuProps({}, { suppressRefError: true })} className="saay-select-menu">
          {isOpen && suggestionItems.length === 0
            ? null
            : suggestionItems.map((item, index) => (
                <li
                  {...getItemProps({ item, index })}
                  className="saay-select-menu__item"
                  data-highlighted={highlightedIndex === index || undefined}
                  key={item}
                >
                  {item}
                </li>
              ))}
        </ul>
      </FloatingList>
      <FieldSupportSlot
        error={error}
        errorId={errorId}
        hint={hint}
        hintId={hintId}
        trailing={<span className="saay-chips-input__counter">{counter}</span>}
      />
    </div>
  );
}

/** Reason suggestion chips that append into an editable text value (owner Q8, I2). */
export function ReasonChips({
  onChange,
  reasons,
  value,
}: {
  readonly onChange: (next: string) => void;
  readonly reasons: readonly string[];
  readonly value: string;
}) {
  return (
    <div className="saay-reason-chips">
      {reasons.map((reason) => (
        <SuggestionChip
          key={reason}
          onClick={() => {
            const trimmed = value.trim();
            onChange(trimmed.length === 0 ? reason : `${trimmed} ${reason}`);
          }}
          type="button"
        >
          {reason}
        </SuggestionChip>
      ))}
    </div>
  );
}
